import { isElectronRuntime } from "./logout";

type NotificationClickHandler = (
  channelId: string,
  messageId?: string,
  workspaceId?: string,
  threadId?: string,
) => void;
let clickHandler: NotificationClickHandler | null = null;

export function setNotificationClickHandler(handler: NotificationClickHandler) {
  clickHandler = handler;
}

export interface NativeNotificationOptions {
  title: string;
  body: string;
  channelId?: string;
  messageId?: string;
  workspaceId?: string;
  threadId?: string;
}

interface ElectronNotifApi {
  fireNotification?: (payload: {
    title: string;
    body: string;
    silent?: boolean;
    clickMetadata?: {
      channelId?: string;
      messageId?: string;
      workspaceId?: string;
      threadId?: string;
    };
  }) => Promise<
    | { ok: true; id: number }
    | { ok: false; reason: "not-supported" }
    | { ok: false; reason: "threw"; error: string }
  >;
  onNotificationClicked?: (
    callback: (payload: {
      id: number;
      channelId?: string;
      messageId?: string;
      workspaceId?: string;
      threadId?: string;
    }) => void,
  ) => () => void;
  focusApp?: () => Promise<unknown>;
}

function getElectronNotifApi(): ElectronNotifApi | null {
  if (typeof window === "undefined") return null;
  const api = (window as unknown as { __electronApi?: ElectronNotifApi })
    .__electronApi;
  return api ?? null;
}

// Subscribe ONCE to main-process click events. Idempotent — multiple
// `sendNativeNotification` calls all funnel through the same dispatcher.
// Without this, the Electron path's clicks would be silently lost.
let electronClickSubscribed = false;
function ensureElectronClickSubscription() {
  if (electronClickSubscribed) return;
  if (!isElectronRuntime()) return;
  const api = getElectronNotifApi();
  if (!api?.onNotificationClicked) return;
  electronClickSubscribed = true;
  api.onNotificationClicked((payload) => {
    if (!payload.channelId || !clickHandler) return;
    clickHandler(
      payload.channelId,
      payload.messageId,
      payload.workspaceId,
      payload.threadId,
    );
  });
}

export async function sendNativeNotification(opts: NativeNotificationOptions) {
  const { title, body, channelId, messageId, workspaceId, threadId } = opts;

  // Electron path (v0.1.62+): route through main process to bypass
  // Chromium's renderer-side Notification permission quirks on macOS.
  // See `apps/desktop-electron/src/main/notifications-host.ts` for
  // the full rationale (electron/electron#11221).
  if (isElectronRuntime()) {
    const api = getElectronNotifApi();
    if (api?.fireNotification) {
      ensureElectronClickSubscription();
      await api.fireNotification({
        title,
        body,
        clickMetadata:
          channelId || messageId || workspaceId || threadId
            ? { channelId, messageId, workspaceId, threadId }
            : undefined,
      });
      return;
    }
    // Fall through to Web API if the IPC bridge is unavailable (e.g.,
    // v0.1.61 desktop that auto-updates to v0.1.62 mid-session — the
    // preload won't have the new methods until the renderer reloads).
  }

  // Web path (and Electron fallback): use the renderer Web API. The
  // browser handles per-origin permission natively.
  if (!("Notification" in window)) return;

  if (Notification.permission !== "granted") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return;
  }

  // In packaged Electron builds, ano-app:// icon URLs may not resolve for OS
  // notifications. Use undefined icon (OS default = app bundle icon) for
  // ano-app://, relative path for http(s)://. `/app-icon.png` is the same
  // 512×512 brand mark used as the desktop bundle icon.
  const icon =
    window.location.protocol === "ano-app:" ? undefined : "/app-icon.png";
  const notif = new Notification(title, { body, icon });

  if (channelId && clickHandler) {
    notif.onclick = () => {
      if (isElectronRuntime()) {
        const electronApi = getElectronNotifApi();
        if (electronApi?.focusApp) electronApi.focusApp().catch(() => {});
      }
      clickHandler!(channelId, messageId, workspaceId, threadId);
    };
  }
}
